import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = "https://gaurav-o0un.onrender.com/api";

const DistributorForm = () => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [formData, setFormData] = useState({
    area_name: '',
    firm_name: '',
    owner_name: '',
    phone: '',
    gstin: '',
    aadhar_number: '',
    firm_address: '',
    godown_address: '',
    year_of_establishment: '',
    other_products: '',
    last_year_turnover: '',
    delivery_mode: '',
    areas_covered: '',
    num_salesmen: '',
    bank_name: '',
    branch_name: '',
    account_number: '',
  });
  const [files, setFiles] = useState({
    gst_certificate: null,
    trade_license: null,
    supporting_docs: null,
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    // Phone validation - 10 digits
    if (!/^\d{10}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    setFiles(prev => ({ ...prev, [fieldName]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!captchaVerified) {
      toast.error(t('error'), { description: 'Please verify the CAPTCHA' });
      return;
    }

    if (!validateForm()) {
      toast.error(t('error'), { description: 'Please fix the errors in the form' });
      return;
    }

    setIsSubmitting(true);
    
    const submitData = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      submitData.append(key, value);
    });
    submitData.append('captcha_verified', captchaVerified);
    
    if (files.gst_certificate) submitData.append('gst_certificate', files.gst_certificate);
    if (files.trade_license) submitData.append('trade_license', files.trade_license);
    if (files.supporting_docs) submitData.append('supporting_docs', files.supporting_docs);

    try {
      await axios.post(`${API}/distributor-enquiry`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('success'), { description: 'Enquiry submitted successfully!' });
      setFormData({
        area_name: '', firm_name: '', owner_name: '', phone: '', gstin: '',
        aadhar_number: '', firm_address: '', godown_address: '', year_of_establishment: '',
        other_products: '', last_year_turnover: '', delivery_mode: '', areas_covered: '',
        num_salesmen: '', bank_name: '', branch_name: '', account_number: '',
      });
      setFiles({ gst_certificate: null, trade_license: null, supporting_docs: null });
      setCaptchaVerified(false);
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || 'Submission failed' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="distributor" className="bg-slate-50 section-spacing" data-testid="distributor-section">
      <div className="container-custom">
        <h2 
          className="text-3xl md:text-5xl font-bold text-[#0047AB] text-center mb-4"
          data-testid="distributor-title"
        >
          {t('distributorTitle')}
        </h2>
        <div className="section-divider mb-12"></div>

        <form onSubmit={handleSubmit} className="form-container" data-testid="distributor-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Area Name */}
            <div className="space-y-2">
              <Label htmlFor="area_name">{t('areaName')} *</Label>
              <Input
                id="area_name"
                name="area_name"
                value={formData.area_name}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-areaname"
              />
            </div>

            {/* Firm Name */}
            <div className="space-y-2">
              <Label htmlFor="firm_name">{t('firmName')} *</Label>
              <Input
                id="firm_name"
                name="firm_name"
                value={formData.firm_name}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-firmname"
              />
            </div>

            {/* Owner Name */}
            <div className="space-y-2">
              <Label htmlFor="owner_name">{t('ownerName')} *</Label>
              <Input
                id="owner_name"
                name="owner_name"
                value={formData.owner_name}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-ownername"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="dist_phone">{t('phone')} *</Label>
              <Input
                id="dist_phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                required
                maxLength={10}
                pattern="\d{10}"
                className={`input-field ${errors.phone ? 'border-red-500' : ''}`}
                data-testid="distributor-phone"
              />
              {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
            </div>

            {/* GSTIN */}
            <div className="space-y-2">
              <Label htmlFor="gstin">{t('gstin')} *</Label>
              <Input
                id="gstin"
                name="gstin"
                value={formData.gstin}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-gstin"
              />
            </div>

            {/* Aadhar Number */}
            <div className="space-y-2">
              <Label htmlFor="aadhar_number">{t('aadharNumber')} *</Label>
              <Input
                id="aadhar_number"
                name="aadhar_number"
                value={formData.aadhar_number}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-aadhar"
              />
            </div>

            {/* Firm Address */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="firm_address">{t('firmAddress')} *</Label>
              <Textarea
                id="firm_address"
                name="firm_address"
                value={formData.firm_address}
                onChange={handleInputChange}
                required
                className="input-field min-h-[80px]"
                data-testid="distributor-firmaddress"
              />
            </div>

            {/* Godown Address */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="godown_address">{t('godownAddress')} *</Label>
              <Textarea
                id="godown_address"
                name="godown_address"
                value={formData.godown_address}
                onChange={handleInputChange}
                required
                className="input-field min-h-[80px]"
                data-testid="distributor-godownaddress"
              />
            </div>

            {/* Year of Establishment */}
            <div className="space-y-2">
              <Label htmlFor="year_of_establishment">{t('yearEstablished')} *</Label>
              <Input
                id="year_of_establishment"
                name="year_of_establishment"
                value={formData.year_of_establishment}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-yearestablished"
              />
            </div>

            {/* Other Products */}
            <div className="space-y-2">
              <Label htmlFor="other_products">{t('otherProducts')}</Label>
              <Input
                id="other_products"
                name="other_products"
                value={formData.other_products}
                onChange={handleInputChange}
                className="input-field"
                data-testid="distributor-otherproducts"
              />
            </div>

            {/* Last Year Turnover */}
            <div className="space-y-2">
              <Label htmlFor="last_year_turnover">{t('lastYearTurnover')} *</Label>
              <Input
                id="last_year_turnover"
                name="last_year_turnover"
                value={formData.last_year_turnover}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-turnover"
              />
            </div>

            {/* Delivery Mode */}
            <div className="space-y-2">
              <Label htmlFor="delivery_mode">{t('deliveryMode')} *</Label>
              <Input
                id="delivery_mode"
                name="delivery_mode"
                value={formData.delivery_mode}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-deliverymode"
              />
            </div>

            {/* Areas Covered */}
            <div className="space-y-2">
              <Label htmlFor="areas_covered">{t('areasCovered')} *</Label>
              <Input
                id="areas_covered"
                name="areas_covered"
                value={formData.areas_covered}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-areascovered"
              />
            </div>

            {/* Number of Salesmen */}
            <div className="space-y-2">
              <Label htmlFor="num_salesmen">{t('numSalesmen')} *</Label>
              <Input
                id="num_salesmen"
                name="num_salesmen"
                value={formData.num_salesmen}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-numsalesmen"
              />
            </div>

            {/* Bank Name */}
            <div className="space-y-2">
              <Label htmlFor="bank_name">{t('bankName')} *</Label>
              <Input
                id="bank_name"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-bankname"
              />
            </div>

            {/* Branch Name */}
            <div className="space-y-2">
              <Label htmlFor="branch_name">{t('branchName')} *</Label>
              <Input
                id="branch_name"
                name="branch_name"
                value={formData.branch_name}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-branchname"
              />
            </div>

            {/* Account Number */}
            <div className="space-y-2">
              <Label htmlFor="account_number">{t('accountNumber')} *</Label>
              <Input
                id="account_number"
                name="account_number"
                value={formData.account_number}
                onChange={handleInputChange}
                required
                className="input-field"
                data-testid="distributor-accountnumber"
              />
            </div>

            {/* File Uploads */}
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* GST Certificate */}
              <div className="space-y-2">
                <Label>{t('uploadGst')}</Label>
                <div className="file-upload border-2 border-dashed border-slate-300 p-4 text-center hover:border-[#0047AB] transition-colors">
                  <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">{files.gst_certificate?.name || 'Click to upload'}</p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'gst_certificate')}
                    data-testid="distributor-gst-upload"
                  />
                </div>
              </div>

              {/* Trade License */}
              <div className="space-y-2">
                <Label>{t('uploadTradeLicense')}</Label>
                <div className="file-upload border-2 border-dashed border-slate-300 p-4 text-center hover:border-[#0047AB] transition-colors">
                  <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">{files.trade_license?.name || 'Click to upload'}</p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'trade_license')}
                    data-testid="distributor-tradelicense-upload"
                  />
                </div>
              </div>

              {/* Supporting Documents */}
              <div className="space-y-2">
                <Label>{t('uploadSupportingDocs')}</Label>
                <div className="file-upload border-2 border-dashed border-slate-300 p-4 text-center hover:border-[#0047AB] transition-colors">
                  <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">{files.supporting_docs?.name || 'Click to upload'}</p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'supporting_docs')}
                    data-testid="distributor-supportingdocs-upload"
                  />
                </div>
              </div>
            </div>

            {/* CAPTCHA */}
            <div className="md:col-span-2">
              <div className="captcha-container">
                <Checkbox
                  id="dist_captcha"
                  checked={captchaVerified}
                  onCheckedChange={setCaptchaVerified}
                  data-testid="distributor-captcha"
                />
                <Label htmlFor="dist_captcha" className="cursor-pointer">{t('captchaVerify')}</Label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="md:col-span-2">
              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={isSubmitting}
                data-testid="distributor-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('submitting')}
                  </>
                ) : (
                  t('submitEnquiry')
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

export default DistributorForm;
